/**
 * 集計関数の実装
 */

import type { DataValue } from '../../../../types/core.types';
import type { AggregationFunction } from '../types';

/**
 * NULL値を除外し、数値に変換
 * boolean値は true=1, false=0 に変換
 */
function convertToNumbers(values: DataValue[]): number[] {
  return values
    .filter(v => v !== null && v !== undefined)
    .map(v => {
      if (typeof v === 'number') return v;
      if (typeof v === 'boolean') return v ? 1 : 0;
      // 文字列の場合は数値に変換を試みる
      if (typeof v === 'string') {
        const num = parseFloat(v);
        if (!isNaN(num)) return num;
      }
      return NaN;
    })
    .filter(v => !isNaN(v));
}

/**
 * SUM集計
 */
function sum(values: DataValue[]): number | null {
  const nums = convertToNumbers(values);
  if (nums.length === 0) return null;
  return nums.reduce((acc, val) => acc + val, 0);
}

/**
 * AVG集計
 */
function avg(values: DataValue[]): number | null {
  const nums = convertToNumbers(values);
  if (nums.length === 0) return null;
  const total = nums.reduce((acc, val) => acc + val, 0);
  return total / nums.length;
}

/**
 * COUNT集計（NULL含む全件数）
 */
function count(values: DataValue[]): number | null {
  return values.length;
}

/**
 * MIN集計
 */
function min(values: DataValue[]): number | null {
  const nums = convertToNumbers(values);
  if (nums.length === 0) return null;
  return Math.min(...nums);
}

/**
 * MAX集計
 */
function max(values: DataValue[]): number | null {
  const nums = convertToNumbers(values);
  if (nums.length === 0) return null;
  return Math.max(...nums);
}

/**
 * 集計関数のマップ
 */
export const aggregationFunctions: Record<AggregationFunction, (values: DataValue[]) => number | null> = {
  sum,
  avg,
  count,
  min,
  max
};

/**
 * 集計関数を実行
 */
export function aggregate(func: AggregationFunction, values: DataValue[]): number | null {
  const aggregator = aggregationFunctions[func];
  if (!aggregator) {
    throw new Error(`Unknown aggregation function: ${func}`);
  }
  return aggregator(values);
}
